import { supabase } from '../lib/supabase';

/**
 * Fetch all warm ups (admin: all, user: published only)
 */
export async function fetchWarmUps({ adminMode = false } = {}) {
  let query = supabase?.from('warm_ups')?.select(`
      id, title, description, instructions,
      mux_playback_id, mux_status, mux_upload_id, mux_asset_id,
      talent_id, view_count, published, difficulty_level, created_by, created_at, updated_at,
      talent:talent_profiles(id, first_name, last_name, display_name, headshot_url)
    `)?.order('created_at', { ascending: false });

  if (!adminMode) {
    query = query?.eq('published', true);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * Fetch a single warm up by ID
 */
export async function fetchWarmUpById(id) {
  const { data, error } = await supabase?.from('warm_ups')?.select(`
      id, title, description, instructions,
      mux_playback_id, mux_status, mux_upload_id, mux_asset_id,
      talent_id, view_count, published, difficulty_level, created_by, created_at, updated_at,
      talent:talent_profiles(id, first_name, last_name, display_name, headshot_url)
    `)?.eq('id', id)?.single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Create a new warm up
 */
export async function createWarmUp({ title, description, instructions, talentId, published = false, difficultyLevel = null, muxPlaybackId = null, muxUploadId = null, muxAssetId = null, muxStatus = 'waiting' }) {
  const { data: { session } } = await supabase?.auth?.getSession();
  if (!session) throw new Error('Not authenticated');

  const { data, error } = await supabase?.from('warm_ups')?.insert({
      title,
      description: description || null,
      instructions: instructions || null,
      talent_id: talentId || null,
      published,
      difficulty_level: difficultyLevel || null,
      mux_playback_id: muxPlaybackId,
      mux_upload_id: muxUploadId,
      mux_asset_id: muxAssetId,
      mux_status: muxStatus,
      created_by: session?.user?.id,
    })?.select()?.single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Update an existing warm up
 */
export async function updateWarmUp(id, updates) {
  const payload = {};
  if (updates?.title !== undefined) payload.title = updates?.title;
  if (updates?.description !== undefined) payload.description = updates?.description;
  if (updates?.instructions !== undefined) payload.instructions = updates?.instructions;
  if (updates?.published !== undefined) payload.published = updates?.published;
  if (updates?.talent_id !== undefined) payload.talent_id = updates?.talent_id;
  if (updates?.talentId !== undefined) payload.talent_id = updates?.talentId;
  if (updates?.difficulty_level !== undefined) payload.difficulty_level = updates?.difficulty_level;
  if (updates?.difficultyLevel !== undefined) payload.difficulty_level = updates?.difficultyLevel;
  if (updates?.mux_playback_id !== undefined) payload.mux_playback_id = updates?.mux_playback_id;
  if (updates?.muxPlaybackId !== undefined) payload.mux_playback_id = updates?.muxPlaybackId;
  if (updates?.mux_upload_id !== undefined) payload.mux_upload_id = updates?.mux_upload_id;
  if (updates?.muxUploadId !== undefined) payload.mux_upload_id = updates?.muxUploadId;
  if (updates?.mux_asset_id !== undefined) payload.mux_asset_id = updates?.mux_asset_id;
  if (updates?.muxAssetId !== undefined) payload.mux_asset_id = updates?.muxAssetId;
  if (updates?.mux_status !== undefined) payload.mux_status = updates?.mux_status;
  if (updates?.muxStatus !== undefined) payload.mux_status = updates?.muxStatus;

  const { data, error } = await supabase?.from('warm_ups')?.update(payload)?.eq('id', id)?.select()?.single();

  if (error) throw new Error(error.message);
  return data;
}

/**
 * Delete a warm up
 */
export async function deleteWarmUp(id) {
  const { error } = await supabase?.from('warm_ups')?.delete()?.eq('id', id);

  if (error) throw new Error(error.message);
}

/**
 * Increment view count for a warm up (fetch + update pattern)
 */
export async function incrementWarmUpViewCount(id) {
  try {
    const { data: current } = await supabase?.from('warm_ups')?.select('view_count')?.eq('id', id)?.single();

    if (current) {
      await supabase?.from('warm_ups')?.update({ view_count: (current?.view_count || 0) + 1 })?.eq('id', id);
    }
  } catch (err) {
    console.warn('Failed to increment view count:', err);
  }
}

/**
 * Fetch all talent profiles for the dropdown
 */
export async function fetchTalentForDropdown() {
  const { data, error } = await supabase?.from('talent_profiles')?.select('id, first_name, last_name, display_name, headshot_url')?.order('first_name');

  if (error) throw new Error(error.message);
  return data || [];
}
